'use client';

import { SoraVideoTooltip } from '@/components/blocks/pricing/SoraVideoTooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { paymentConfig } from '@/config/payment.config';
import { useRouter } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import { getSubscriptionInfo } from '@/server/actions/credit-actions';
import { createCheckoutSession } from '@/server/actions/payment/create-subscription';
import { getUserSubscription } from '@/server/actions/payment/get-billing-info';
import { updateSubscriptionAction } from '@/server/actions/payment/update-subscription';
import { trackPricingClick } from '@/server/actions/user-tracking-actions';
import { useIsAuthenticated } from '@/store/auth-store';
import { Alibaba, Google, Grok, Qwen, Runway, Sora } from '@lobehub/icons';
import { Check, ChevronDown, ChevronUp, Info, Minus, Plus, Sparkles } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { toast } from 'sonner';
import { BillingToggle } from './BillingToggle';
import { PlanSelectorTable } from './PlanSelectorTable';
import { PricingComparisonCard } from './PricingComparisonCard';

// Static service costs for the credit usage guide tooltip
const STATIC_SERVICES = [
  {
    id: '1',
    name: 'Sora 2 720p 10s',
    slug: 'sora-2-720p-10s',
    description: null,
    creditCost: 30,
    isActive: true,
  },
  {
    id: '2',
    name: 'Sora 2 720p 15s',
    slug: 'sora-2-720p-15s',
    description: null,
    creditCost: 45,
    isActive: true,
  },
  {
    id: '3',
    name: 'Sora 2 Pro 720p 10s',
    slug: 'sora-2-pro-720p-10s',
    description: null,
    creditCost: 150,
    isActive: true,
  },
  {
    id: '4',
    name: 'Sora 2 Pro 720p 15s',
    slug: 'sora-2-pro-720p-15s',
    description: null,
    creditCost: 270,
    isActive: true,
  },
  {
    id: '5',
    name: 'Sora 2 Pro 1080p 10s',
    slug: 'sora-2-pro-1080p-10s',
    description: null,
    creditCost: 330,
    isActive: true,
  },
  {
    id: '6',
    name: 'Sora 2 Pro 1080p 15s',
    slug: 'sora-2-pro-1080p-15s',
    description: null,
    creditCost: 630,
    isActive: true,
  },
];

const OPENAI_COST_PER_VIDEO = 0.75; // £0.75 based on $1.00 for 10s

interface PlanSelectorProps {
  className?: string;
  externalBillingInterval?: 'one-time' | 'month' | 'year' | 'week';
  onBillingIntervalChange?: (interval: 'one-time' | 'month' | 'year' | 'week') => void;
  hideBillingToggle?: boolean;
  showPriceComparison?: boolean;
  isCompact?: boolean;
}

export function PlanSelector({
  className,
  externalBillingInterval,
  onBillingIntervalChange,
  hideBillingToggle = false,
  showPriceComparison = true,
  isCompact = false,
}: PlanSelectorProps) {
  const TRIAL_ENABLED = false;

  const router = useRouter();
  const isAuthenticated = useIsAuthenticated();
  const [selectedPlan, setSelectedPlan] = useState(1); // Default to tier-w3 (index 1 - tier-w1 hidden)
  const [localBillingInterval, setLocalBillingInterval] = useState<
    'one-time' | 'month' | 'year' | 'week'
  >('week');

  const billingInterval = externalBillingInterval || localBillingInterval;
  const setBillingInterval = onBillingIntervalChange || setLocalBillingInterval;

  const isYearly = billingInterval === 'year';
  const isOneTime = billingInterval === 'one-time';
  const isWeek = billingInterval === 'week';
  const [currency, setCurrency] = useState<'gbp' | 'usd' | 'eur'>('gbp');
  const [hasClaimedTrial, setHasClaimedTrial] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [currentInterval, setCurrentInterval] = useState<string | null>(null);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  const plans = useMemo(
    () =>
      paymentConfig.plans.filter((p) => {
        if (p.hidden) return false;
        if (billingInterval === 'one-time') return !!p.stripePriceIds?.oneTime;
        if (billingInterval === 'year') return p.interval === 'month'; // monthly plans have yearly stripe IDs
        return p.interval === billingInterval;
      }),
    [billingInterval]
  );
  const activePlan = plans[selectedPlan] || plans[0];

  const currencySymbol = currency === 'usd' ? '$' : currency === 'eur' ? '€' : '£';
  const getDisplayPrice = (p_value: number, yp_value: number) => {
    let basePrice = p_value;
    if (billingInterval === 'year') basePrice = yp_value;
    if (billingInterval === 'one-time') basePrice = Math.floor(p_value * 1.3);
    if (billingInterval === 'week') basePrice = p_value; // weekly plans use their own price field

    // Rough UI estimates for multi-currency display
    if (currency === 'usd') return Math.floor(basePrice * 1.25);
    if (currency === 'eur') return Math.floor(basePrice * 1.18);
    return basePrice;
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!isAuthenticated) return;

      try {
        const [billingResult, subResult] = await Promise.all([
          getUserSubscription(),
          getSubscriptionInfo(),
        ]);

        if (billingResult.success && billingResult.data) {
          setHasClaimedTrial(billingResult.data.hasClaimedTrial);
        }

        if (subResult.success && subResult.data) {
          const subInfo = subResult.data;
          setCurrentPlanId(subInfo.planId);
          setCurrentInterval(subInfo.interval);

          // Pre-select the plan if found
          const planIdx = plans.findIndex((p) => p.id === subInfo.planId);

          if (planIdx !== -1) {
            setSelectedPlan(planIdx);
            setBillingInterval(
              subInfo.interval === 'year'
                ? 'year'
                : (subInfo.interval as string) === 'week'
                  ? 'week'
                  : 'month'
            );
          }
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      }
    };

    fetchSubscription();
  }, [isAuthenticated, plans, setBillingInterval]);

  const selectedPriceId =
    billingInterval === 'year'
      ? activePlan?.stripePriceIds?.yearly
      : billingInterval === 'week'
        ? activePlan?.stripePriceIds?.weekly
        : billingInterval === 'one-time'
          ? (activePlan?.stripePriceIds as any)?.oneTime
          : activePlan?.stripePriceIds?.monthly;

  const handlePurchase = () => {
    if (!isAuthenticated) {
      router.push('/?auth=login');
      return;
    }

    if (!selectedPriceId) {
      toast.error('Configuration error: Price ID not found');
      return;
    }

    startTransition(async () => {
      try {
        // Tracking: Mark pricing click and stripe start
        trackPricingClick(activePlan?.id).catch((err) =>
          console.error('Failed to track pricing click', err)
        );

        // If already subscribed, update or reload
        if (currentPlanId) {
          const updateResult = await updateSubscriptionAction({
            priceId: selectedPriceId,
          });
          if (updateResult.success) {
            toast.success('Plan updated successfully! Your credits have been refreshed.');
            router.refresh();
            return;
          }
          // If update fails because no active sub (remnant), fall through to checkout
        }

        const checkoutResult = await createCheckoutSession({
          priceId: selectedPriceId,
          quantity: 1,
          trialDays: TRIAL_ENABLED && !hasClaimedTrial ? 7 : undefined,
          currency,
        });

        if (checkoutResult.success && checkoutResult.data?.url) {
          window.location.href = checkoutResult.data.url;
        } else {
          toast.error(checkoutResult.error || 'Failed to start checkout');
        }
      } catch (error) {
        toast.error('An unexpected error occurred');
        console.error(error);
      }
    });
  };

  const getButtonText = () => {
    if (!isAuthenticated || !currentPlanId) {
      if (!TRIAL_ENABLED) return 'Get Started';
      return hasClaimedTrial ? 'Activate Plan' : 'Reload Now';
    }

    const isCurrentPlanSelected =
      currentPlanId === activePlan?.id &&
      ((billingInterval === 'year' && currentInterval === 'year') ||
        (billingInterval === 'month' && currentInterval === 'month') ||
        (billingInterval === 'week' && currentInterval === 'week'));

    if (isCurrentPlanSelected) {
      return 'Reload Credits';
    }

    return 'Switch Plan';
  };

  const handleNextPlan = () => {
    if (selectedPlan < plans.length - 1) {
      setSelectedPlan((prev) => prev + 1);
    }
  };

  const handlePrevPlan = () => {
    if (selectedPlan > 0) {
      setSelectedPlan((prev) => prev - 1);
    }
  };

  // Trial date calculation (7 days from now)
  const trialDate = new Date();
  trialDate.setDate(trialDate.getDate() + 7);
  const formattedTrialDate = trialDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Calculate current state based on toggle
  const currentCredits = isYearly
    ? activePlan?.credits?.yearly || 0
    : isWeek
      ? activePlan?.credits?.weekly || 0
      : activePlan?.credits?.monthly || 0;
  const displayPrice = isYearly ? activePlan?.yearlyPrice || 0 : activePlan?.price || 0;
  const priceTerm = isYearly ? 'year' : isWeek ? 'week' : 'month';

  // Feature Cleanup: Remove first 2 duplicated features
  const activeFeatures =
    isYearly && activePlan?.yearlyFeatures ? activePlan.yearlyFeatures : activePlan?.features || [];
  const polishedFeatures = activeFeatures.slice(2);

  return (
    <div
      className={cn(
        'relative flex w-full max-w-[480px] flex-col items-center overflow-visible',
        className
      )}
    >
      {/* Faint purple radial gradient behind card */}
      <div className="-translate-x-1/2 -translate-y-1/2 -z-10 pointer-events-none absolute top-1/2 left-1/2 h-[150%] w-[150%] bg-[radial-gradient(circle,rgba(168,85,247,0.05)_0%,transparent_70%)]" />
      <Card
        className={cn(
          'relative h-full w-full overflow-visible rounded-3xl border border-zinc-300 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white to-gray-100 shadow-none transition-shadow dark:border-zinc-600 dark:bg-[#2f2f2f]',
          className?.includes('h-full') && 'flex h-full flex-col'
        )}
      >
        <div className="absolute top-6 right-5 z-40 hidden">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as any)}
            className="cursor-pointer rounded-md border border-zinc-200 bg-zinc-100 px-2 py-1 font-medium text-xs text-zinc-700 outline-none transition-colors hover:bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
          >
            <option value="gbp">GBP</option>
            <option value="usd">USD</option>
            <option value="eur">EUR</option>
          </select>
        </div>
        {/* Abstract triangle outline guiding to the button */}
        <div className="pointer-events-none absolute inset-0 z-0 hidden overflow-hidden rounded-3xl">
          <svg
            className="-translate-x-1/2 absolute top-0 left-1/2 h-[70%] w-[220%] opacity-10 dark:opacity-35"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
          >
            <path
              d="M -35 0 L 135 0 L 50 100 Z"
              fill="none"
              stroke="url(#triangle-gradient)"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
            <defs>
              <linearGradient id="triangle-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0" />
                <stop offset="40%" stopColor="#8b5cf6" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="1" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        {!hideBillingToggle && (
          <div className="-top-[18px] -translate-x-1/2 absolute left-1/2 z-30">
            <BillingToggle
              billingInterval={billingInterval}
              setBillingInterval={setBillingInterval}
            />
          </div>
        )}
        <CardContent className="relative z-10 px-5 pt-8 pb-0 text-left">
          <div className="mb-8 flex flex-col items-center px-1 text-center">
            <div className="relative flex w-full flex-col items-center">
              <div className="break-max-width -mt-2 -mx-[19.5px] md:-mx-[19px] relative mb-6 w-[calc(100%+39px)] max-w-none overflow-hidden md:w-[calc(100%+37px)]">
                <style
                  dangerouslySetInnerHTML={{
                    __html: `
                  @keyframes marqueeMobile {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                  }
                  @keyframes marqueeDesktop {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                  }
                  .animate-marquee {
                    animation: marqueeMobile 25s linear infinite;
                    will-change: transform;
                  }
                  @media (min-width: 768px) {
                    .animate-marquee {
                      animation: marqueeDesktop 40s linear infinite;
                    }
                  }
                `,
                  }}
                />
                <div
                  className="flex min-w-max animate-marquee flex-nowrap"
                  style={{ WebkitFontSmoothing: 'antialiased' }}
                >
                  {[1, 2, 3, 4].map((set) => (
                    <div
                      key={`set-${set}`}
                      className="flex min-w-max flex-nowrap items-center gap-4 pr-4"
                      style={{ flexShrink: 0 }}
                    >
                      <div
                        className="flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-400 bg-white/95 px-2.5 py-1.5 subpixel-antialiased shadow-sm dark:border-zinc-500/50 dark:bg-zinc-800"
                        style={{ flexShrink: 0 }}
                      >
                        <Sora.Avatar size={14} />
                        <span className="whitespace-nowrap font-semibold text-[10px] text-gray-700 tracking-tight dark:text-gray-300">
                          Sora 2
                        </span>
                      </div>
                      <div
                        className="flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-400 bg-white/95 px-2.5 py-1.5 subpixel-antialiased shadow-sm dark:border-zinc-500/50 dark:bg-zinc-800"
                        style={{ flexShrink: 0 }}
                      >
                        <Runway.Avatar size={14} />
                        <span className="whitespace-nowrap font-semibold text-[10px] text-gray-700 tracking-tight dark:text-gray-300">
                          Runway Gen-4
                        </span>
                      </div>
                      <div
                        className="flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-400 bg-white/95 px-2.5 py-1.5 subpixel-antialiased shadow-sm dark:border-zinc-500/50 dark:bg-zinc-800"
                        style={{ flexShrink: 0 }}
                      >
                        <Qwen.Avatar size={14} />
                        <span className="whitespace-nowrap font-semibold text-[10px] text-gray-700 tracking-tight dark:text-gray-300">
                          Z-Image
                        </span>
                      </div>
                      <div
                        className="flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-400 bg-white/95 px-2.5 py-1.5 subpixel-antialiased shadow-sm dark:border-zinc-500/50 dark:bg-zinc-800"
                        style={{ flexShrink: 0 }}
                      >
                        <Google.Avatar size={14} />
                        <span className="whitespace-nowrap font-semibold text-[10px] text-gray-700 tracking-tight dark:text-gray-300">
                          Nano Banana 2
                        </span>
                      </div>
                      <div
                        className="flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-400 bg-white/95 px-2.5 py-1.5 subpixel-antialiased shadow-sm dark:border-zinc-500/50 dark:bg-zinc-800"
                        style={{ flexShrink: 0 }}
                      >
                        <Grok.Avatar size={14} />
                        <span className="whitespace-nowrap font-semibold text-[10px] text-gray-700 tracking-tight dark:text-gray-300">
                          Grok Imagine
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent dark:from-[#121212]" />
                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent dark:from-[#121212]" />
              </div>

              <div className="mx-auto flex w-max max-w-full flex-col items-start">
                <div className="-mt-2 col-span-2 mb-1 w-full text-center font-bold text-[#a1a1aa] text-[10px] uppercase tracking-[0.2em] sm:text-xs">
                  Up to
                </div>

                <div className="grid w-full grid-cols-[auto_1fr] items-baseline gap-x-1.5 gap-y-0 text-left">
                  {/* Row 1 */}
                  <div className="text-right font-extrabold text-4xl text-gray-900 leading-none tracking-tighter sm:text-7xl min-[375px]:text-6xl dark:text-white">
                    {Math.floor(
                      (isYearly
                        ? activePlan?.credits?.yearly || 0
                        : isWeek
                          ? activePlan?.credits?.weekly || 0
                          : activePlan?.credits?.monthly || 0) / 30
                    ).toLocaleString()}
                  </div>
                  <div className="font-black text-gray-900 text-lg uppercase tracking-tighter min-[375px]:text-xl dark:text-white">
                    <span className="relative inline-block">
                      <div className="-top-[26px] -translate-x-1/2 absolute left-1/2 text-gray-900 opacity-90 dark:text-white">
                        <Sora size={24} />
                      </div>
                      Videos
                    </span>
                  </div>

                  {/* Row 2 - Or */}
                  <div className="col-span-2 mt-4 mb-1 w-full text-center font-bold text-[#a1a1aa] text-[10px] uppercase tracking-[0.2em] sm:text-xs">
                    Or
                  </div>

                  {/* Row 3 - Images */}
                  <div className="text-right font-extrabold text-[28px] text-gray-900 leading-none tracking-tighter sm:text-[44px] min-[375px]:text-[38px] dark:text-white">
                    {Math.floor(
                      (isYearly
                        ? activePlan?.credits?.yearly || 0
                        : isWeek
                          ? activePlan?.credits?.weekly || 0
                          : activePlan?.credits?.monthly || 0) / 0.8
                    ).toLocaleString()}
                  </div>
                  <div className="font-black text-gray-900 text-lg uppercase tracking-tighter min-[375px]:text-xl dark:text-white">
                    <span className="relative inline-block">
                      <div className="-top-[16px] -translate-x-1/2 absolute left-1/2 text-gray-900 opacity-90 dark:text-white">
                        <Qwen size={16} />
                      </div>
                      Images
                    </span>
                  </div>

                  <div className="col-start-2 mt-0 mb-3 text-left">
                    <div className="inline-block font-black text-2xl text-gray-900 tracking-tighter min-[375px]:text-3xl dark:text-white">
                      {currencySymbol}
                      {getDisplayPrice(activePlan?.price || 0, activePlan?.yearlyPrice || 0)}
                      <span className="ml-1 font-bold text-[12px] opacity-70 sm:text-sm">
                        {isYearly ? '/YR' : isWeek ? '/WK' : isOneTime ? 'ONCE' : '/MO'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mx-auto mt-2 select-none font-medium text-[10px] text-gray-500 uppercase tracking-wider xs:tracking-widest dark:text-zinc-300">
                  {(isYearly
                    ? activePlan?.credits?.yearly || 0
                    : isWeek
                      ? activePlan?.credits?.weekly || 0
                      : activePlan?.credits?.monthly || 0
                  ).toLocaleString()}{' '}
                  total credits
                </div>
                <div className="h-0 overflow-hidden opacity-0">
                  <SoraVideoTooltip
                    planCredits={currentCredits}
                    services={STATIC_SERVICES}
                    planName={activePlan?.name || ''}
                    isYearly={isYearly}
                  />
                </div>
              </div>

              <div className="sm:-mx-2 md:-mx-3 pointer-events-none absolute inset-y-0 right-0 left-0 flex items-center justify-between px-0.5">
                <button
                  onClick={handlePrevPlan}
                  disabled={selectedPlan === 0}
                  aria-label="Previous plan tier"
                  className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border-2 border-zinc-600 bg-white text-gray-900 shadow-sm transition-all enabled:cursor-pointer enabled:active:scale-95 enabled:hover:scale-110 enabled:hover:border-zinc-800 enabled:hover:shadow-md disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-300 sm:h-12 sm:w-12 dark:border-white/40 dark:bg-zinc-800 dark:text-white dark:disabled:border-white/10 dark:disabled:bg-zinc-800/50 dark:disabled:text-zinc-600 dark:enabled:hover:scale-110 dark:enabled:hover:border-white/70 dark:enabled:hover:bg-zinc-700 dark:enabled:hover:shadow-md"
                >
                  <Minus className="h-4 w-4 stroke-[3] sm:h-5 sm:w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={handleNextPlan}
                  disabled={selectedPlan === plans.length - 1}
                  aria-label="Next plan tier"
                  className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border-2 border-zinc-600 bg-white text-gray-900 shadow-sm transition-all enabled:cursor-pointer enabled:active:scale-95 enabled:hover:scale-110 enabled:hover:border-zinc-800 enabled:hover:shadow-md disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-300 sm:h-12 sm:w-12 dark:border-white/40 dark:bg-zinc-800 dark:text-white dark:disabled:border-white/10 dark:disabled:bg-zinc-800/50 dark:disabled:text-zinc-600 dark:enabled:hover:scale-110 dark:enabled:hover:border-white/70 dark:enabled:hover:bg-zinc-700 dark:enabled:hover:shadow-md"
                >
                  <Plus className="h-4 w-4 stroke-[3] sm:h-5 sm:w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <PlanSelectorTable
            plans={plans}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            isTableOpen={isTableOpen}
            setIsTableOpen={setIsTableOpen}
            billingInterval={billingInterval}
            isYearly={isYearly}
            isOneTime={isOneTime}
            isWeek={isWeek}
            currencySymbol={currencySymbol}
            getDisplayPrice={getDisplayPrice}
          />

          {currentPlanId && (
            <div className="-mx-2 relative z-10 mt-4 mb-2 flex min-h-[90px] flex-col items-center justify-center">
              <Button
                className="btn-purple md:!text-[20px] sm:!text-[18px] !text-[16px] relative mb-1 h-14 w-auto overflow-hidden rounded-full px-14 xs:px-16 font-semibold text-white shadow-[0_4px_14px_0_rgba(106,79,177,0.3)] transition-all hover:scale-[1.02] hover:shadow-[0_6px_20px_rgba(106,79,177,0.23)] sm:h-16 dark:shadow-none"
                style={{
                  background: 'linear-gradient(to bottom, #7C61BD, #6A4FB1)',
                }}
                onClick={handlePurchase}
                disabled={isPending}
              >
                {getButtonText()}
              </Button>
              <div className="mt-2 font-medium text-[10px] text-gray-500 uppercase tracking-tight dark:text-zinc-400">
                {currentPlanId === activePlan?.id
                  ? 'Refresh your credits instantly'
                  : 'Change your plan settings'}
              </div>
            </div>
          )}
          {!currentPlanId && TRIAL_ENABLED && (
            <>
              <div className="-mx-0 group/trial relative mt-4 flex min-h-[140px] flex-col items-center justify-center overflow-hidden rounded-2xl bg-indigo-100/90 p-6.5 dark:bg-indigo-950/50">
                {/* Decorative background element - centered to guide eye down */}
                <div className="-translate-x-1/2 -mb-16 pointer-events-none absolute bottom-0 left-1/2 h-32 w-48 rounded-full bg-indigo-500/5 blur-2xl dark:bg-indigo-400/5" />

                <div className="relative z-10 mb-3 flex flex-wrap items-baseline justify-center gap-1.5">
                  <span className="font-normal text-[12px] text-gray-500 dark:text-zinc-300">
                    Try
                  </span>
                  <span className="font-bold text-gray-900 text-xl leading-none underline decoration-4 decoration-indigo-600 underline-offset-4 min-[375px]:text-2xl dark:text-white">
                    Free
                  </span>
                  <span className="font-normal text-[12px] text-gray-500 dark:text-zinc-300">
                    for 7 days
                  </span>
                </div>

                <div className="relative z-10 mt-4 mb-3 w-full text-center font-bold text-[12px] text-gray-900 uppercase tracking-wide dark:text-white">
                  Due Today: {currency.toUpperCase()} 0.00
                </div>

                <Button
                  className="btn-purple md:!text-[22px] sm:!text-[20px] !text-[18px] relative z-10 mb-1 h-14 w-auto overflow-hidden rounded-full px-14 xs:px-16 font-semibold text-white shadow-[0_4px_14px_0_rgba(106,79,177,0.3)] transition-all hover:scale-[1.02] hover:shadow-[0_6px_20px_rgba(106,79,177,0.23)] sm:h-16 dark:shadow-none"
                  style={{
                    background: 'linear-gradient(to bottom, #7C61BD, #6A4FB1)',
                  }}
                  onClick={handlePurchase}
                  disabled={isPending}
                >
                  {getButtonText()}
                </Button>
              </div>
              <div className="relative z-10 mt-8 mb-4 flex w-full flex-col items-start bg-white dark:bg-[#121212]">
                <div className="w-full border-zinc-100 border-t pt-6 text-left font-normal text-[12px] text-gray-500 leading-[1.6] dark:border-zinc-800 dark:text-zinc-300">
                  *
                  <Link
                    href="/terms"
                    className="!text-gray-500 dark:!text-zinc-300 hover:!text-gray-600 dark:hover:!text-gray-300 font-medium underline transition-colors"
                  >
                    Free Trial T&amp;Cs
                  </Link>
                  . Pay nothing until{' '}
                  <span className="font-semibold text-gray-500 dark:text-zinc-200">
                    {(() => {
                      const d = new Date(Date.now() + 7 * 86400000);
                      const day = d.getDate();
                      const th =
                        day > 3 && day < 21 ? 'th' : ['th', 'st', 'nd', 'rd'][day % 10] || 'th';
                      return `${d.toLocaleDateString('en-GB', { month: 'short' })} ${day}${th}`;
                    })()}
                  </span>
                  , then Sora-2 Creator Pass (up to{' '}
                  {Math.floor(
                    (isYearly
                      ? activePlan?.credits?.yearly || 0
                      : activePlan?.credits?.monthly || 0) / 30
                  ).toLocaleString()}{' '}
                  Videos {isYearly ? 'Yearly' : isWeek ? 'Weekly' : 'Monthly'}){' '}
                  <span className="font-semibold text-gray-500 dark:text-zinc-200">
                    {' '}
                    £{isYearly ? activePlan?.yearlyPrice || 0 : activePlan?.price || 0}
                  </span>
                  , Auto-renews.{' '}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help border-zinc-400 border-b border-dotted transition-colors hover:border-zinc-600 dark:border-zinc-500 dark:hover:border-zinc-300">
                          Cancel anytime in 1-click.{' '}
                          <span className="inline-flex translate-y-[2px] items-center justify-center rounded-full bg-black/5 p-0.5 align-text-bottom transition-colors hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10">
                            <Info
                              className="h-3 w-3 text-gray-500 dark:text-gray-400"
                              aria-hidden="true"
                            />
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[200px] text-center">
                        One-click cancellation available directly from your dashboard anytime. No
                        emails needed.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </>
          )}

          {!currentPlanId && activePlan && !TRIAL_ENABLED && (
            <div className="-mx-2 relative z-10 mt-4 mb-0 flex flex-col items-center justify-center">
              <Button
                className="btn-purple md:!text-[20px] sm:!text-[18px] !text-[16px] relative mb-1 h-14 w-auto overflow-hidden rounded-full px-14 xs:px-16 font-semibold text-white shadow-[0_4px_14px_0_rgba(106,79,177,0.3)] transition-all hover:scale-[1.02] hover:shadow-[0_6px_20px_rgba(106,79,177,0.23)] sm:h-16 dark:shadow-none"
                style={{
                  background: 'linear-gradient(to bottom, #7C61BD, #6A4FB1)',
                }}
                onClick={handlePurchase}
                disabled={isPending}
              >
                {getButtonText()}
              </Button>
              <div className="mt-2 font-medium text-[10px] text-gray-500 uppercase tracking-tight dark:text-zinc-400">
                {billingInterval === 'one-time'
                  ? 'Easy Pay As You Go, Long Life Credits'
                  : `Cancel anytime. Auto-renews ${isYearly ? 'yearly' : isWeek ? 'weekly' : 'monthly'}.`}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showPriceComparison && (
        <PricingComparisonCard
          currentPlanId={currentPlanId}
          isYearly={isYearly}
          isWeek={isWeek}
          activePlan={activePlan}
          currentCredits={
            isYearly
              ? activePlan?.credits?.yearly || 0
              : isWeek
                ? activePlan?.credits?.weekly || 0
                : activePlan?.credits?.monthly || 0
          }
          displayPrice={getDisplayPrice(activePlan?.price || 0, activePlan?.yearlyPrice || 0)}
          currencySymbol={currencySymbol}
          selectedPlan={selectedPlan}
          OPENAI_COST_PER_VIDEO={OPENAI_COST_PER_VIDEO}
        />
      )}
    </div>
  );
}
